import { useSessionManagementServiceV1 } from "@rw1/api-services";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  toApiMetricFilter,
  toCumulativeMetricSeries,
} from "../../../../domain/mappers/metricsMappers";
import type { Dimension, MetricQuery } from "../../../../domain/models/metrics";
import { GC_TIME_MS, STALE_TIME_MS } from "../../../../hooks/constants";
import { normalizeMetricDateRange } from "../../utils/metricDateRange";
import {
  anchorActivityTrendRangeStart,
  buildActivityTrendChartData,
  isActivityTrendDataEmpty,
  toActivityTrendSeries,
  type ActivityTrendDataPoint,
} from "../../utils/trendChartDataUtils";

export const ACTIVITY_TREND_METRICS_QUERY_KEY = "activityTrendMetrics";

interface UseActivityTrendDataParams {
  startDate: Date;
  endDate: Date;
  dimension: Dimension;
  contentMetadataIds?: string[];
  countries?: string[];
  minSessionDurationInMinutes?: number;
  enabled?: boolean;
}

export interface UseActivityTrendDataResult {
  chartData: ActivityTrendDataPoint[];
  isEmpty: boolean;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
}

export const useActivityTrendData = ({
  startDate,
  endDate,
  dimension,
  contentMetadataIds,
  countries,
  minSessionDurationInMinutes,
  enabled = true,
}: UseActivityTrendDataParams): UseActivityTrendDataResult => {
  const sessionManagementService = useSessionManagementServiceV1();

  const { companyId } = useParams();

  const normalizedQuery = useMemo<MetricQuery>(
    () => ({
      ...normalizeMetricDateRange(startDate, endDate),
      dimension,
      contentMetadataIds,
      countries,
      minSessionDurationInMinutes,
    }),
    [
      startDate,
      endDate,
      dimension,
      contentMetadataIds,
      countries,
      minSessionDurationInMinutes,
    ],
  );

  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: [
      ACTIVITY_TREND_METRICS_QUERY_KEY,
      companyId,
      normalizedQuery.startDate.toISOString(),
      normalizedQuery.endDate.toISOString(),
      normalizedQuery.dimension,
      normalizedQuery.contentMetadataIds,
      normalizedQuery.countries,
      normalizedQuery.minSessionDurationInMinutes,
    ],
    queryFn: async () => {
      if (!companyId) {
        throw new Error("Company ID is required");
      }

      const result = await sessionManagementService.getCumulativeMetrics(
        companyId,
        toApiMetricFilter(normalizedQuery),
      );

      return toActivityTrendSeries(toCumulativeMetricSeries(result));
    },
    enabled: !!companyId && enabled,
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    placeholderData: keepPreviousData,
    retry: 1,
  });

  const chartData = useMemo<ActivityTrendDataPoint[]>(
    () =>
      anchorActivityTrendRangeStart(
        buildActivityTrendChartData(
          normalizedQuery.startDate,
          normalizedQuery.endDate,
          normalizedQuery.dimension,
          data ?? {},
        ),
      ),
    [data, normalizedQuery],
  );

  const isEmpty = useMemo(
    () => isActivityTrendDataEmpty(chartData),
    [chartData],
  );

  return {
    chartData,
    isEmpty,
    isLoading,
    isFetching,
    error,
  };
};
